import { useAuth } from "../../../context/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, Check, User, X, XCircle } from "lucide-react";
import { getWorkflowRun, WorkflowNode, WorkflowRunItem, WorkflowVersion } from "../api";
import { buildJourneyCards, formatWhen, nodeLabel } from "../history-display";

interface CallerJourneyDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  caller: WorkflowRunItem | null;
  workflowName?: string;
  nodes?: WorkflowNode[];
  versions?: WorkflowVersion[];
}

function statusPresentation(status?: string) {
  if (status === "completed") {
    return { label: "✓ Completed", className: "bg-success-soft text-success border-success/30" };
  }
  if (status === "ended_no_match") {
    return { label: "⚠️ Did not match", className: "bg-warning-soft text-warning border-warning/30" };
  }
  if (status === "failed" || status === "exhausted") {
    return { label: status === "failed" ? "✕ Failed" : "⚠️ Limit reached", className: "bg-danger-soft text-danger border-danger/30" };
  }
  return { label: "⏳ In progress", className: "bg-info-soft text-info border-info/30" };
}

export function CallerJourneyDrawer({
  isOpen,
  onClose,
  caller,
  workflowName = "Workflow",
  nodes = [],
  versions = [],
}: CallerJourneyDrawerProps) {
  const { user } = useAuth() as any;
  const userTz = user?.timezone;
  const { data: detail, isLoading } = useQuery({
    queryKey: ["workflowRun", caller?.run_id],
    queryFn: () => getWorkflowRun(caller!.run_id),
    enabled: isOpen && Boolean(caller?.run_id),
  });

  if (!isOpen || !caller) return null;

  const run = detail || caller;
  const pinned = versions.find((version) => version.workflow_version === run.workflow_version);
  const graphNodes = pinned?.nodes?.length ? pinned.nodes : nodes;
  const cards = buildJourneyCards(run, graphNodes);
  const phone = run.customer_phone || run.contact_id || "Unknown Caller";
  const name = run.contact_name || `Contact ${run.contact_id || run.run_id}`;
  const status = statusPresentation(run.status);
  const currentStage = nodeLabel(graphNodes, run.current_node_id);

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-ink/40 backdrop-blur-xs transition-opacity animate-in fade-in">
      <div className="absolute inset-y-0 right-0 flex max-w-full pl-10">
        <div className="w-screen max-w-md bg-elevated shadow-2xl flex flex-col border-l border-line animate-in slide-in-from-right duration-300">
          <div className="flex items-center justify-between px-6 py-5 border-b border-line bg-soft/50">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-info-soft text-info border border-info/30 font-bold">
                <User className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-ink leading-snug truncate max-w-[260px]">{name}</h2>
                <p className="text-xs text-ink-3 font-mono">{phone}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="rounded-lg p-1.5 text-ink-3 hover:bg-soft hover:text-ink-2 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <div className="px-6 py-4 border-b border-line bg-elevated">
            <div className="rounded-xl border border-line bg-soft/50 p-4">
              <div className="text-[11px] font-bold tracking-wider uppercase text-ink-3">Current Step</div>
              <div className="flex items-center justify-between mt-1 gap-3">
                <span className="text-sm font-bold text-ink truncate">{currentStage || run.current_node_id}</span>
                <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold shrink-0 ${status.className}`}>
                  {status.label}
                </span>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-5">
            {isLoading && !detail ? (
              <p className="text-xs text-ink-3">Loading caller history…</p>
            ) : (
              <div>
                <div className="text-[11px] font-extrabold uppercase tracking-wider text-ink-3 mb-3">
                  Timeline Progression
                </div>
                <div className="relative pl-6 flex flex-col gap-4">
                  <div className="absolute left-[7px] top-2 bottom-2 w-0.5 bg-line" />
                  {cards.map((card) => (
                    <div key={card.key} className="relative">
                      <div
                        className={`absolute -left-6 top-0.5 flex h-4 w-4 items-center justify-center rounded-full border-2 ${
                          card.pending
                            ? "bg-info-soft border-info text-info"
                            : card.blocked
                              ? "bg-danger-soft border-danger text-danger"
                              : "bg-success-soft border-success text-success"
                        }`}
                      >
                        {card.blocked ? <XCircle className="h-2.5 w-2.5" /> : <Check className="h-2.5 w-2.5" />}
                      </div>
                      {card.at && (
                        <div className="text-[11px] font-semibold text-ink-3">{formatWhen(card.at, userTz)}</div>
                      )}
                      <div className="text-[13px] font-bold text-ink">{card.nodeLabel || card.nodeId}</div>
                      {card.agentLabel && (
                        <div className="text-[11px] text-ink-3 font-mono">{card.agentLabel}</div>
                      )}
                      {card.pending && (
                        <p className="mt-1 text-[11.5px] text-ink-2">Waiting for the first call on this agent to complete.</p>
                      )}
                      {card.outcomes.length > 0 && (
                        <ul className="mt-2 space-y-1.5">
                          {card.outcomes.map((outcome) => (
                            <li
                              key={`${card.key}-${outcome.field}`}
                              className={`rounded-lg border px-2.5 py-1.5 text-[11.5px] ${
                                outcome.tone === "green"
                                  ? "border-success/30 bg-success-soft/40 text-success"
                                  : outcome.tone === "red"
                                    ? "border-danger/30 bg-danger-soft/40 text-danger"
                                    : "border-line bg-soft text-ink-2"
                              }`}
                            >
                              <div className="font-semibold">
                                {outcome.label} = <span className="font-mono">{outcome.value}</span>
                              </div>
                              {outcome.tone === "red" && outcome.expected ? (
                                <div className="mt-0.5 text-[10.5px] opacity-90">
                                  Expected {outcome.expected}
                                  {outcome.actual ? ` · filled ${outcome.actual}` : ""}
                                </div>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                      {card.blocked && card.blockedCopy && (
                        <p className="mt-2 text-[11.5px] font-semibold text-danger leading-relaxed">
                          {card.blockedCopy}
                        </p>
                      )}
                      {card.movedToLabel && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[11.5px] font-semibold text-success">
                          <ArrowRight className="h-3 w-3" />
                          Moved to {card.movedToLabel}
                          {card.scheduledFor ? ` · ${formatWhen(card.scheduledFor, userTz)}` : ""}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-line px-6 py-4 bg-soft/50 flex items-center justify-between">
            <span className="text-xs text-ink-3 truncate pr-3">{workflowName}</span>
            <button
              onClick={onClose}
              className="rounded-lg border border-line bg-elevated px-4 py-2 text-xs font-semibold text-ink-2 shadow-xs hover:bg-soft transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
